import tabbable from "tabbable";
import logger from "utils/logger";

const debugTag = "[KEYBOARD-TRAP]";

/**
 * Keeps track of all of the initialized KeyboardTrap instances
 * @type {Array}
 */
export const keyboardTraps = [];

/**
 * Contains the necessary logic to implement a keyboard trap
 */
export default class KeyboardTrap {

  /**
   * Creates a KeyboardTrap instance and initializes it
   * @param {Array<Object>} selectors - An array of obects in this format:
   * [{item: HTMLElement, type: "element"}, {item: HTMLElement, type: "container"}]
   * Where items of type "element" represent individual HTMLElements and items
   * of type "container" represent root nodes, which will be traversed in order
   * to find all of their focusable descendents.
   * @param {Boolean} reFetch - Whether to re-fetch the list of tabbable
   * elements on every focus event (this is expensive but accutate)
   */
  constructor(selectors, reFetch = true) {
    keyboardTraps.push(this);
    logger.info(`${debugTag}[${this.getIndex()}] init`);
    this.tabbableNodeSelectors = selectors;
    this.getTabbableNodesOnKeyPress = reFetch;
    this.onClickBound = this.onClick.bind(this);
    this.onFocusBound = this.onFocus.bind(this);
    this.onKeyDownBound = this.onKeyDown.bind(this);
    this.onKeyUpBound = this.onKeyUp.bind(this);
    this.setDefaultState();
  }

  /**
   * Resets the keyboard trap back to its default state
   */
  setDefaultState() {
    this.tabbableNodes = [];
    this.focusedEl = null;
    this.previouslyFocusedEl = null;
    this.active = false;
    this.keyboardActive = false;
    this.shiftKeyPressed = false;
  }

  /**
   * Activates the keyboard trap
   */
  activate() {
    logger.info(`${debugTag}[${this.getIndex()}] activate`);
    // There should only be one active keyboard trap at a time
    keyboardTraps.forEach(trap => {
      if (trap !== this && trap.active) {
        trap.deactivate();
      }
    });
    document.addEventListener("click", this.onClickBound, true);
    document.addEventListener("focus", this.onFocusBound, true);
    document.addEventListener("keydown", this.onKeyDownBound, true);
    document.addEventListener("keyup", this.onKeyUpBound, true);
    this.getTabbableNodes();
    this.active = true;
  }

  /**
   * Deactivates the keyboard trap
   */
  deactivate() {
    logger.info(`${debugTag}[${this.getIndex()}] deactivate`);
    document.removeEventListener("click", this.onClickBound, true);
    document.removeEventListener("focus", this.onFocusBound, true);
    document.removeEventListener("keydown", this.onKeyDownBound, true);
    document.removeEventListener("keyup", this.onKeyUpBound, true);
    this.setDefaultState();
  }

  /**
   * Keeps track of the user's input method so that we can later infer
   * whether a focus change was triggered by a mouse click or a key press
   */
  onClick() {
    this.keyboardActive = false;
  }

  /**
   * Keeps track of the user's input method so that we can later infer
   * whether a focus change was triggered by a mouse click or a key press
   * @param {KeyboardEvent} event - A keyboard event
   */
  onKeyDown(event) {
    if (event.key === "Tab") {
      this.keyboardActive = true;
    }
    this.shiftKeyPressed = event.shiftKey;
  }

  /**
   * Keeps track of the user's input method so that we can later infer
   * whether a focus change was triggered by a mouse click or a key press
   * @param {KeyboardEvent} event - A keyboard event
   */
  onKeyUp(event) {
    if (event.key === "Tab") {
      this.keyboardActive = false;
    }
    this.shiftKeyPressed = event.shiftKey;
  }

  /**
   * If the focus changed as a result of the Tab key being pressed, determines
   * whether the user tabbed onto an element that's outside of the allowed
   * list. If so, then redirects focus to the appropriate element.
   * @param {FocusEvent} event - A focus event
   */
  onFocus(event) {
    // Note: document.activeElement is the element that's ABOUT TO receive focus
    this.previouslyFocusedEl = this.focusedEl;
    this.focusedEl = document.activeElement;

    // Determine whether a keyboard or mouse initiated the focus change
    if (!this.keyboardActive) {
      return;
    }
    // Refresh tabbable node list in case DOM has changed since last tab press
    if (this.getTabbableNodesOnKeyPress) {
      this.getTabbableNodes();
    }
    if (!this.tabbableNodes.includes(this.focusedEl)) {
      event.preventDefault();
      logger.info(`${debugTag}[${this.getIndex()}] intercepted`, this.focusedEl);

      if (this.tabbableNodes.includes(this.previouslyFocusedEl)) {
        const index = this.tabbableNodes.indexOf(this.previouslyFocusedEl);
        // If shift key is pressed then we can assume user is tabbing backwards
        if (this.shiftKeyPressed) {
          const prev = this.getPrevTabbableNode(index);
          prev.focus();
          logger.info(`${debugTag}[${this.getIndex()}] focused (prev node)`, prev);
        } else {
          const next = this.getNextTabbableNode(index);
          next.focus();
          logger.info(`${debugTag}[${this.getIndex()}] focused (next node)`, next);
        }
      } else {
        this.tabbableNodes[0].focus();
        logger.info(`${debugTag}[${this.getIndex()}] focused (first node)`, this.tabbableNodes[0]);
      }
    } else {
      logger.info(`${debugTag}[${this.getIndex()}] allowed`);
    }
  }

  /**
   * Creates an array of tabbable elements, based on the provided selectors
   */
  getTabbableNodes() {
    this.tabbableNodes = [];
    this.tabbableNodeSelectors.forEach(obj => {
      if ((obj.type !== "container" && obj.type !== "element") || !obj.item) {
        return;
      }
      if (obj.type === "container") {
        this.tabbableNodes = this.tabbableNodes.concat(tabbable(obj.item));
      } else {
        this.tabbableNodes.push(obj.item);
      }
    });
  }

  /**
   * Finds the next tabbable element in the list
   * @param {Number} index - The starting index
   * @returns {HTMLElement} - The next tabbable element in the list
   * or the first element, if we've cycled all the way to the end
   */
  getNextTabbableNode(index) {
    if ((index + 1) > (this.tabbableNodes.length - 1)) {
      return this.tabbableNodes[0];
    }
    return this.tabbableNodes[index + 1];
  }

  /**
   * Finds the previous tabbable element in the list
   * @param {Number} index - The starting index
   * @returns {HTMLElement} - The previous tabbable element in the list
   * or the last element, if we've cycled all the way back to the start
   */
  getPrevTabbableNode(index) {
    if ((index - 1) < 0) {
      return this.tabbableNodes[this.tabbableNodes.length - 1];
    }
    return this.tabbableNodes[index - 1];
  }

  /**
   * Finds the index of the current keyboard trap instance in the global array
   * @returns {Number} - The index of the keyboard trap that's associated with
   * the current class instance
   */
  getIndex() {
    return keyboardTraps.findIndex(trap => trap === this);
  }

};
